/** 违禁词库管理：复用列表、分页和弹窗，按语言维护投放内容词条。 */
import {
  type ProhibitedTermDto,
  type ProhibitedTermInput,
  ProhibitedTermInputSchema,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useProhibitedTerms } from '~/data/queries';
import { prohibitedTermsApi } from '~/hooks/use-prohibited-terms';
import { useAuthStore } from '~/stores/auth-store';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { FormRow } from '~/ui/FormRow';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { Switch } from '~/ui/Switch';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { SectionHead } from './settings-shared';
const EMPTY: ProhibitedTermInput = {
  term: '',
  language: 'en',
  illegal_type: '',
  match_mode: 'whole_word',
  enabled: true,
  notes: '',
};
function Editor({
  row,
  onClose,
  onSaved,
}: { row: ProhibitedTermDto | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<ProhibitedTermInput>(
    row
      ? {
          term: row.term,
          language: row.language,
          illegal_type: row.illegal_type,
          match_mode: row.match_mode,
          enabled: row.enabled,
          notes: row.notes,
        }
      : EMPTY,
  );
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: (body: ProhibitedTermInput) =>
      row ? prohibitedTermsApi.update(row.id, body) : prohibitedTermsApi.create(body),
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });
  const submit = () => {
    const p = ProhibitedTermInputSchema.safeParse(form);
    if (!p.success) {
      setError(p.error.issues[0]?.message ?? '输入错误');
      return;
    }
    setError('');
    save.mutate(p.data);
  };
  return (
    <Dialog
      title={row ? '编辑违禁词' : '新增违禁词'}
      onClose={onClose}
      width={620}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button className="btn btn-accent" disabled={save.isPending} onClick={submit}>
            保存
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow label="词语 / 短语" required>
          <input
            aria-label="词语 / 短语"
            className="input"
            value={form.term}
            onChange={(e) => setForm({ ...form, term: e.target.value })}
          />
        </FormRow>
        <FormRow label="语言" required hint="如 en、pl、cs、ar、ru；* 适用于全部语言">
          <input
            aria-label="词条语言"
            className="input mono"
            value={form.language}
            onChange={(e) => setForm({ ...form, language: e.target.value })}
          />
        </FormRow>
        <FormRow label="违规类别" required>
          <input
            aria-label="违规类别"
            className="input"
            value={form.illegal_type}
            onChange={(e) => setForm({ ...form, illegal_type: e.target.value })}
          />
        </FormRow>
        <FormRow label="匹配方式">
          <select
            aria-label="匹配方式"
            className="input"
            value={form.match_mode}
            onChange={(e) =>
              setForm({ ...form, match_mode: e.target.value as ProhibitedTermInput['match_mode'] })
            }
          >
            <option value="whole_word">完整词 / 短语</option>
            <option value="substring">包含匹配</option>
          </select>
        </FormRow>
        <FormRow label="启用">
          <Switch checked={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} />
        </FormRow>
        <FormRow label="备注">
          <textarea
            aria-label="备注"
            className="input"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </FormRow>
        {error && (
          <div role="alert" style={{ color: 'var(--neg)' }}>
            {error}
          </div>
        )}
      </div>
    </Dialog>
  );
}
function ImportDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [text, setText] = useState('');
  const [language, setLanguage] = useState('en');
  const [category, setCategory] = useState('');
  const [matchMode, setMatchMode] = useState<ProhibitedTermInput['match_mode']>('whole_word');
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: prohibitedTermsApi.import,
    onSuccess: (r) => {
      toast(`已录入 ${r.imported} 条，跳过 ${r.skipped} 条重复词`, { tone: 'pos' });
      onSaved();
    },
    onError: (e) => setError(e.message),
  });
  const submit = () => {
    const lines = text
      .split(/\r?\n/)
      .map((x) => x.trim())
      .filter(Boolean);
    if (lines.length < 1 || lines.length > 500) {
      setError('每次录入 1–500 行');
      return;
    }
    const items: ProhibitedTermInput[] = [];
    for (const term of lines) {
      const p = ProhibitedTermInputSchema.safeParse({
        ...EMPTY,
        term,
        language,
        illegal_type: category,
        match_mode: matchMode,
      });
      if (!p.success) {
        setError(p.error.issues[0]?.message ?? '输入错误');
        return;
      }
      items.push(p.data);
    }
    save.mutate(items);
  };
  return (
    <Dialog
      title="批量录入违禁词"
      onClose={onClose}
      width={620}
      footer={
        <button className="btn btn-accent" disabled={save.isPending} onClick={submit}>
          录入
        </button>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow label="语言" required>
          <input
            aria-label="批量语言"
            className="input"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          />
        </FormRow>
        <FormRow label="违规类别" required>
          <input
            aria-label="批量违规类别"
            className="input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
        </FormRow>
        <FormRow label="匹配方式" hint="中文等无空格分词的文本建议包含匹配">
          <select
            aria-label="批量匹配方式"
            className="input"
            value={matchMode}
            onChange={(e) => setMatchMode(e.target.value as ProhibitedTermInput['match_mode'])}
          >
            <option value="whole_word">完整词 / 短语</option>
            <option value="substring">包含匹配</option>
          </select>
        </FormRow>
        <FormRow label="词条" required hint="每行一个词语或短语，最多 500 行；重复词跳过">
          <textarea
            aria-label="批量词条"
            className="input"
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </FormRow>
        {error && (
          <div role="alert" style={{ color: 'var(--neg)' }}>
            {error}
          </div>
        )}
      </div>
    </Dialog>
  );
}
export default function ProhibitedTermsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role === 'root' || role === 'admin';
  const [search, setSearch] = useState('');
  const [language, setLanguage] = useState('');
  const [enabled, setEnabled] = useState('');
  const [page, setPage] = useState(1);
  const [editor, setEditor] = useState<ProhibitedTermDto | null | undefined>();
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState<ProhibitedTermDto | null>(null);
  const q = useProhibitedTerms({
    q: search || undefined,
    language: language || undefined,
    enabled: enabled || undefined,
    offset: (page - 1) * 20,
    limit: 20,
  });
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['prohibited-terms'] });
  const saved = () => {
    setEditor(undefined);
    setImporting(false);
    void refresh();
  };
  const remove = useMutation({
    mutationFn: prohibitedTermsApi.remove,
    onSuccess: () => {
      setDeleting(null);
      void refresh();
    },
    onError: (e) => toast(e.message, { tone: 'neg' }),
  });
  return (
    <>
      <SectionHead
        title="违禁词库"
        sub="按语言与违规类别维护，作用于落地页投放内容；原书正文保留"
        action={
          canManage ? (
            <div className="hstack" style={{ gap: 8 }}>
              <button className="btn" onClick={() => setImporting(true)}>
                批量录入
              </button>
              <button className="btn btn-accent" onClick={() => setEditor(null)}>
                新增词条
              </button>
            </div>
          ) : undefined
        }
      />
      <div className="hstack" style={{ gap: 10 }}>
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v.target.value);
            setPage(1);
          }}
          placeholder="搜索词语或短语…"
        />
        <input
          aria-label="语言筛选"
          className="input mono"
          style={{ maxWidth: 150 }}
          value={language}
          placeholder="语言，如 en"
          onChange={(e) => {
            setLanguage(e.target.value);
            setPage(1);
          }}
        />
        <FilterButton
          label="状态"
          multi={false}
          value={enabled ? [enabled] : []}
          options={[
            { value: 'true', label: '启用' },
            { value: 'false', label: '停用' },
          ]}
          onChange={(values) => {
            setEnabled(values[0] ?? '');
            setPage(1);
          }}
        />
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        {q.isError ? (
          <div role="alert">{q.error.message}</div>
        ) : (
          <Table className="tbl">
            <thead>
              <tr>
                <th>词语 / 短语</th>
                <th>语言</th>
                <th>违规类别</th>
                <th>匹配方式</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {q.isPending ? (
                <tr>
                  <td colSpan={6}>加载中…</td>
                </tr>
              ) : q.data.items.length === 0 ? (
                <tr>
                  <td colSpan={6}>暂无词条</td>
                </tr>
              ) : (
                q.data.items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.term}</td>
                    <td className="mono">{row.language}</td>
                    <td>{row.illegal_type}</td>
                    <td>{row.match_mode === 'whole_word' ? '完整词 / 短语' : '包含匹配'}</td>
                    <td>{row.enabled ? '启用' : '停用'}</td>
                    <td>
                      {canManage && (
                        <div className="hstack" style={{ gap: 8 }}>
                          <button className="btn btn-sm" onClick={() => setEditor(row)}>
                            编辑
                          </button>
                          <button className="btn btn-sm" onClick={() => setDeleting(row)}>
                            删除
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>
      <Pagination
        total={q.data?.total ?? 0}
        page={page}
        pageSize={20}
        onChange={setPage}
        sizeOptions={[20]}
      />
      {editor !== undefined && (
        <Editor row={editor} onClose={() => setEditor(undefined)} onSaved={saved} />
      )}
      {importing && <ImportDialog onClose={() => setImporting(false)} onSaved={saved} />}
      {deleting && (
        <ConfirmDialog
          title="删除词条"
          message={`删除「${deleting.term}」后，不再参与后续任务的词库匹配。`}
          tone="danger"
          onConfirm={() => remove.mutate(deleting.id)}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}
